import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl, thumbUrl, themeAudioUrl, getAdminToken, clearAdminToken, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function Admin() {
  const nav = useNavigate();
  const [tab, setTab] = useState("upload");
  const [sections, setSections] = useState([]);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  const reload = () => setReloadKey((k) => k + 1);

  useEffect(() => {
    if (!getAdminToken()) {
      nav("/admin/login", { replace: true });
      return;
    }
    let cancelled = false;
    Promise.all([api.listSections(), api.listMedia()])
      .then(([s, m]) => {
        if (cancelled) return;
        setSections(s);
        setMedia(m);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e?.response?.status === 401) {
          clearAdminToken();
          nav("/admin/login", { replace: true });
        } else {
          toast.error(formatApiError(e));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [nav, reloadKey]);

  const logout = () => {
    clearAdminToken();
    nav("/admin/login", { replace: true });
  };

  return (
    <div className="grain min-h-screen flex bg-[var(--fsyl-bg)]" data-testid="admin-page">
      {/* Sidebar */}
      <aside className="w-60 shrink-0 border-r border-white/5 bg-[#080809] p-6 flex flex-col gap-2">
        <div className="font-display font-black text-2xl tracking-tighter mb-2">FYSL</div>
        <div className="font-mono-fsyl text-[9px] tracking-[0.3em] text-[var(--fsyl-dim)] mb-8">// ADMIN CONSOLE</div>

        {[
          ["upload", "Upload"],
          ["library", "Library"],
          ["sections", "Sections"],
          ["theme", "Theme Song"],
          ["settings", "Passcode"],
        ].map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`admin-tab-${k}`}
            className={`text-left font-mono-fsyl text-[11px] tracking-[0.25em] uppercase px-3 py-2.5 border ${
              tab === k ? "border-[var(--fsyl-cyan)] text-[var(--fsyl-cyan)] bg-white/[0.02]" : "border-transparent text-[var(--fsyl-muted)] hover:text-white"
            } transition`}
          >
            {label}
          </button>
        ))}
        <div className="mt-auto pt-6 border-t border-white/5 space-y-2">
          <button onClick={() => nav("/portfolio")} data-testid="admin-view-site" className="w-full text-left font-mono-fsyl text-[10px] tracking-widest text-[var(--fsyl-muted)] hover:text-white px-3 py-2">↗ VIEW SITE</button>
          <button onClick={logout} data-testid="admin-logout" className="w-full text-left font-mono-fsyl text-[10px] tracking-widest text-red-400 hover:text-red-300 px-3 py-2">LOGOUT</button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 p-8 md:p-12 overflow-y-auto h-screen">
        {loading ? (
          <div className="font-mono-fsyl text-[12px] tracking-widest text-[var(--fsyl-muted)]">LOADING...</div>
        ) : (
          <>
            {tab === "upload" && <UploadTab sections={sections} media={media} onUploaded={reload} />}
            {tab === "library" && <LibraryTab media={media} sections={sections} reload={reload} />}
            {tab === "sections" && <SectionsTab sections={sections} reload={reload} />}
            {tab === "theme" && <ThemeTab />}
            {tab === "settings" && <PasscodeTab />}
          </>
        )}
      </main>
    </div>
  );
}

const UploadTab = ({ sections, media, onUploaded }) => {
  const [sectionSlug, setSectionSlug] = useState("");
  const [vault, setVault] = useState(false);
  const [entries, setEntries] = useState([]); // { localId, file, previewUrl, title, reflection, tags, camera, capturedAt, status, error }
  const [busy, setBusy] = useState(false);

  const scopedSections = useMemo(
    () => sections.filter(s => (s.scope || "main") === (vault ? "vault" : "main")),
    [sections, vault]
  );
  const selectedSlug = (sectionSlug && scopedSections.find(s => s.slug === sectionSlug))
    ? sectionSlug
    : (scopedSections[0]?.slug || "");

  // Show what's already in the picked section (matching destination scope)
  const existingInSection = useMemo(() => {
    if (!media || !selectedSlug) return [];
    return media.filter(m =>
      m.section_slug === selectedSlug && (!!m.vault) === vault
    );
  }, [media, selectedSlug, vault]);

  const addFiles = (filesList) => {
    const incoming = Array.from(filesList || []);
    if (incoming.length === 0) return;
    const newEntries = incoming.map((f) => ({
      localId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      file: f,
      previewUrl: URL.createObjectURL(f),
      kind: f.type.startsWith("video/") ? "video" : "image",
      title: "",
      reflection: "",
      tags: "",
      camera: "",
      capturedAt: "",
      status: "pending", // pending | uploading | done | error
      error: "",
    }));
    setEntries(prev => [...prev, ...newEntries]);
  };

  const updateEntry = (localId, patch) => {
    setEntries(prev => prev.map(e => e.localId === localId ? { ...e, ...patch } : e));
  };

  const removeEntry = (localId) => {
    setEntries(prev => {
      const target = prev.find(e => e.localId === localId);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter(e => e.localId !== localId);
    });
  };

  const clearAll = () => {
    entries.forEach(e => e.previewUrl && URL.revokeObjectURL(e.previewUrl));
    setEntries([]);
  };

  const pendingCount = entries.filter(e => e.status !== "done").length;

  const uploadAll = async () => {
    if (entries.length === 0 || !selectedSlug) {
      toast.error("Add files and pick a section");
      return;
    }
    setBusy(true);
    let okCount = 0;
    let failCount = 0;
    const toUpload = entries.filter(e => e.status !== "done");

    const tryOne = async (e, attempt = 1) => {
      const fd = new FormData();
      fd.append("file", e.file);
      fd.append("section_slug", selectedSlug);
      if (e.title) fd.append("title", e.title);
      if (e.reflection) fd.append("reflection", e.reflection);
      if (e.tags) fd.append("tags", e.tags);
      if (e.camera) fd.append("camera", e.camera);
      if (e.capturedAt) fd.append("captured_at", e.capturedAt);
      if (vault) fd.append("vault", "true");
      try {
        await api.uploadMedia(fd);
        return { ok: true };
      } catch (err) {
        const status = err?.response?.status;
        const transient = !status || status === 0 || status === 408 || status === 429 || status >= 500;
        if (transient && attempt < 3) {
          await new Promise(r => setTimeout(r, 500 * attempt));
          return tryOne(e, attempt + 1);
        }
        return { ok: false, error: formatApiError(err) };
      }
    };

    for (const e of toUpload) {
      updateEntry(e.localId, { status: "uploading", error: "" });
      // small breath between requests so the backend can settle GridFS writes
      const result = await tryOne(e);
      if (result.ok) {
        updateEntry(e.localId, { status: "done" });
        okCount += 1;
      } else {
        updateEntry(e.localId, { status: "error", error: result.error || "Upload failed" });
        failCount += 1;
      }
      await new Promise(r => setTimeout(r, 150));
    }

    setBusy(false);
    if (okCount > 0) toast.success(`Uploaded ${okCount} ${okCount === 1 ? "entry" : "entries"}`);
    if (failCount > 0) toast.error(`${failCount} failed — see the rows below. Click upload again to retry.`);
    onUploaded();
  };

  return (
    <div className="max-w-5xl">
      <div className="font-mono-fsyl text-[10px] tracking-[0.4em] text-[var(--fsyl-cyan)] mb-3">/ UPLOAD</div>
      <h1 className="font-display font-medium text-4xl tracking-tighter mb-8">Add new entries</h1>

      {/* Destination + section: shared for the whole batch */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
        <div>
          <div className="font-mono-fsyl text-[10px] tracking-[0.3em] text-[var(--fsyl-muted)] mb-3">DESTINATION</div>
          <div className="inline-flex border border-white/10 bg-[var(--fsyl-surface)] p-1" role="tablist">
            <button
              type="button"
              onClick={() => setVault(false)}
              data-testid="upload-dest-main"
              className={`font-mono-fsyl text-[11px] tracking-[0.3em] uppercase px-5 py-2.5 transition ${
                !vault ? "bg-white/10 text-white" : "text-[var(--fsyl-muted)] hover:text-white"
              }`}
            >
              Main Archive
            </button>
            <button
              type="button"
              onClick={() => setVault(true)}
              data-testid="upload-dest-vault"
              className={`font-mono-fsyl text-[11px] tracking-[0.3em] uppercase px-5 py-2.5 transition ${
                vault ? "bg-[var(--fsyl-cyan)]/15 text-[var(--fsyl-cyan)]" : "text-[var(--fsyl-muted)] hover:text-white"
              }`}
            >
              ◌ Vault
            </button>
          </div>
          <div className="mt-3 font-mono-fsyl text-[9px] tracking-widest" data-testid="upload-dest-hint">
            {vault ? (
              <span className="text-[var(--fsyl-cyan)]">// PRIVATE — VISIBLE ONLY WITH VAULT PASSCODE</span>
            ) : (
              <span className="text-[var(--fsyl-dim)]">// PUBLIC — VISIBLE ON THE MAIN PORTFOLIO</span>
            )}
          </div>
        </div>

        <div>
          <div className="font-mono-fsyl text-[10px] tracking-[0.3em] text-[var(--fsyl-muted)] mb-3">SECTION (APPLIES TO ALL)</div>
          <select
            value={selectedSlug}
            onChange={(e) => setSectionSlug(e.target.value)}
            data-testid="upload-section"
            className="w-full bg-[var(--fsyl-surface)] border border-white/10 px-3 py-3 font-mono-fsyl text-sm"
            disabled={scopedSections.length === 0}
          >
            {scopedSections.length === 0 ? (
              <option value="">No {vault ? "vault" : "main"} sections yet — create one first</option>
            ) : (
              scopedSections.map(s => <option key={s.id} value={s.slug}>{s.name}</option>)
            )}
          </select>
          <div className="mt-2 font-mono-fsyl text-[9px] tracking-widest text-[var(--fsyl-dim)]" data-testid="section-existing-count">
            {selectedSlug ? (
              existingInSection.length === 0
                ? `// EMPTY — YOUR UPLOADS WILL BE THE FIRST HERE`
                : `// ALREADY IN THIS SECTION: ${existingInSection.length} ${existingInSection.length === 1 ? "ENTRY" : "ENTRIES"}`
            ) : ""}
          </div>
        </div>
      </div>

      {/* Existing entries preview strip */}
      {selectedSlug && existingInSection.length > 0 && (
        <div className="mb-8" data-testid="section-existing-strip">
          <div className="font-mono-fsyl text-[10px] tracking-[0.3em] text-[var(--fsyl-muted)] mb-3">
            ALREADY IN {selectedSlug.toUpperCase()}
          </div>
          <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-2">
            {existingInSection.map(m => (
              <div key={m.id} className="shrink-0 w-24 h-32 bg-black border border-white/5 relative overflow-hidden" title={m.title || m.section_slug}>
                {m.kind === "video" ? (
                  <div className="w-full h-full flex items-center justify-center font-mono-fsyl text-[9px] tracking-widest text-[var(--fsyl-muted)]">▶</div>
                ) : (
                  <img
                    src={thumbUrl(m.id, m.vault ? "vault" : "site", 240)}
                    alt={m.title || ""}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                    draggable={false}
                  />
                )}
                {m.title && (
                  <div className="absolute bottom-0 inset-x-0 bg-black/70 px-1 py-0.5 font-mono-fsyl text-[8px] tracking-widest text-white truncate">
                    {m.title}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* File picker */}
      <div className="mb-8">
        <label
          htmlFor="upload-file-input"
          className="block border border-dashed border-white/15 hover:border-[var(--fsyl-cyan)] hover:bg-white/[0.02] transition cursor-pointer p-8 text-center"
          data-testid="upload-dropzone"
        >
          <div className="font-mono-fsyl text-[10px] tracking-[0.4em] text-[var(--fsyl-cyan)] mb-2">+ ADD FILES</div>
          <div className="font-reflection text-base text-[var(--fsyl-muted)]">
            Click to pick one or many photos &amp; videos
          </div>
          <input
            id="upload-file-input"
            type="file"
            multiple
            accept="image/*,video/*"
            onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
            data-testid="upload-file"
            className="hidden"
          />
        </label>
      </div>

      {/* Per-file rows */}
      {entries.length > 0 && (
        <div className="space-y-4 mb-8">
          {entries.map((e) => (
            <EntryRow
              key={e.localId}
              entry={e}
              onChange={(patch) => updateEntry(e.localId, patch)}
              onRemove={() => removeEntry(e.localId)}
              disabled={busy}
            />
          ))}
        </div>
      )}

      {/* Action bar */}
      {entries.length > 0 && (
        <div className="flex items-center justify-between pt-4 border-t border-white/5">
          <div className="font-mono-fsyl text-[10px] tracking-widest text-[var(--fsyl-muted)]">
            {entries.length} {entries.length === 1 ? "FILE" : "FILES"} READY
            {pendingCount !== entries.length && ` — ${entries.length - pendingCount} DONE`}
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={clearAll}
              disabled={busy}
              data-testid="upload-clear"
              className="font-mono-fsyl text-[10px] tracking-[0.3em] uppercase px-4 py-2.5 border border-white/10 text-[var(--fsyl-muted)] hover:text-white disabled:opacity-40"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={uploadAll}
              disabled={busy || pendingCount === 0}
              data-testid="upload-submit"
              className="font-mono-fsyl text-[11px] tracking-[0.3em] uppercase px-6 py-3 border border-white/15 hover:border-[var(--fsyl-cyan)] hover:text-[var(--fsyl-cyan)] transition disabled:opacity-40"
            >
              {busy ? "UPLOADING..." : `→ Upload ${pendingCount} to ${vault ? "Vault" : "Archive"}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const EntryRow = ({ entry, onChange, onRemove, disabled }) => {
  const statusColor =
    entry.status === "done" ? "text-[var(--fsyl-cyan)]" :
    entry.status === "uploading" ? "text-amber-400" :
    entry.status === "error" ? "text-red-400" :
    "text-[var(--fsyl-dim)]";
  const statusLabel =
    entry.status === "done" ? "✓ UPLOADED" :
    entry.status === "uploading" ? "● UPLOADING..." :
    entry.status === "error" ? "✕ ERROR" :
    "○ PENDING";

  return (
    <div className="bg-[var(--fsyl-elev)] border border-white/5 p-4 grid grid-cols-1 md:grid-cols-[140px_1fr_40px] gap-4" data-testid={`upload-entry-${entry.localId}`}>
      {/* Thumb */}
      <div className="w-full h-32 md:h-full bg-black overflow-hidden flex items-center justify-center">
        {entry.kind === "video" ? (
          <video
            src={entry.previewUrl}
            controls
            playsInline
            preload="metadata"
            className="w-full h-full object-contain"
            data-testid={`entry-video-preview-${entry.localId}`}
          />
        ) : (
          <img src={entry.previewUrl} alt="" className="w-full h-full object-cover" />
        )}
      </div>

      {/* Fields */}
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="font-mono-fsyl text-[9px] tracking-widest text-[var(--fsyl-dim)] truncate flex-1 min-w-0">
            {entry.file.name} · {(entry.file.size / (1024 * 1024)).toFixed(1)} MB
          </div>
          <div className={`font-mono-fsyl text-[9px] tracking-widest ${statusColor}`}>{statusLabel}</div>
        </div>
        {entry.status === "error" && entry.error && (
          <div className="font-mono-fsyl text-[10px] tracking-widest text-red-400">{entry.error}</div>
        )}

        <input
          type="text"
          value={entry.title}
          onChange={(e) => onChange({ title: e.target.value })}
          placeholder="Title (optional)"
          disabled={disabled}
          data-testid={`entry-title-${entry.localId}`}
          className="w-full bg-[var(--fsyl-surface)] border border-white/10 px-3 py-2 font-mono-fsyl text-sm"
        />
        <textarea
          value={entry.reflection}
          onChange={(e) => onChange({ reflection: e.target.value })}
          placeholder="Reflection / story (optional)"
          rows={2}
          disabled={disabled}
          data-testid={`entry-reflection-${entry.localId}`}
          className="w-full bg-[var(--fsyl-surface)] border border-white/10 px-3 py-2 font-reflection text-base leading-relaxed"
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <input
            type="text"
            value={entry.tags}
            onChange={(e) => onChange({ tags: e.target.value })}
            placeholder="tags, comma, sep"
            disabled={disabled}
            data-testid={`entry-tags-${entry.localId}`}
            className="bg-[var(--fsyl-surface)] border border-white/10 px-3 py-2 font-mono-fsyl text-xs"
          />
          <input
            type="text"
            value={entry.camera}
            onChange={(e) => onChange({ camera: e.target.value })}
            placeholder="Camera"
            disabled={disabled}
            data-testid={`entry-camera-${entry.localId}`}
            className="bg-[var(--fsyl-surface)] border border-white/10 px-3 py-2 font-mono-fsyl text-xs"
          />
          <input
            type="text"
            value={entry.capturedAt}
            onChange={(e) => onChange({ capturedAt: e.target.value })}
            placeholder="Date"
            disabled={disabled}
            data-testid={`entry-date-${entry.localId}`}
            className="bg-[var(--fsyl-surface)] border border-white/10 px-3 py-2 font-mono-fsyl text-xs"
          />
        </div>
      </div>

      {/* Remove */}
      <button
        type="button"
        onClick={onRemove}
        disabled={disabled || entry.status === "uploading"}
        data-testid={`entry-remove-${entry.localId}`}
        className="self-start font-mono-fsyl text-[10px] tracking-widest text-[var(--fsyl-dim)] hover:text-red-400 disabled:opacity-30"
      >
        ✕
      </button>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block font-mono-fsyl text-[10px] tracking-[0.3em] text-[var(--fsyl-muted)] mb-2">{label}</span>
    {children}
  </label>
);

const LibraryTab = ({ media, sections, reload }) => {
  const [editing, setEditing] = useState(null);
  const [previewIdx, setPreviewIdx] = useState(null); // index into `items`
  const [filter, setFilter] = useState("all");
  const [destFilter, setDestFilter] = useState("all");
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [localOrder, setLocalOrder] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const [dragId, setDragId] = useState(null);

  // When media reloads, drop any optimistic order (server is now source of truth)
  useEffect(() => { setLocalOrder(null); }, [media]);

  const orderedMedia = useMemo(() => {
    if (!localOrder) return media;
    const map = new Map(media.map(m => [m.id, m]));
    const out = [];
    for (const id of localOrder) {
      const m = map.get(id);
      if (m) { out.push(m); map.delete(id); }
    }
    // append any new items not in localOrder
    for (const m of map.values()) out.push(m);
    return out;
  }, [media, localOrder]);

  const items = useMemo(() => {
    return orderedMedia.filter(m => {
      if (filter !== "all" && m.section_slug !== filter) return false;
      if (destFilter === "main" && m.vault) return false;
      if (destFilter === "vault" && !m.vault) return false;
      return true;
    });
  }, [orderedMedia, filter, destFilter]);

  const counts = useMemo(() => ({
    main: media.filter(m => !m.vault).length,
    vault: media.filter(m => m.vault).length,
  }), [media]);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await api.deleteMedia(pendingDelete.id);
      toast.success("Entry deleted");
      setPendingDelete(null);
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleting(false);
    }
  };

  const persistOrder = async (newOrderedFull) => {
    setSavingOrder(true);
    try {
      const orders = newOrderedFull.map((m, i) => ({ id: m.id, order: i }));
      await api.reorderMedia(orders);
      toast.success("Order saved");
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
      setLocalOrder(null);
    } finally {
      setSavingOrder(false);
    }
  };

  const moveItem = (fromId, toId) => {
    if (!fromId || fromId === toId) return;
    const ids = orderedMedia.map(m => m.id);
    const fromIdx = ids.indexOf(fromId);
    const toIdx = ids.indexOf(toId);
    if (fromIdx === -1 || toIdx === -1) return;
    ids.splice(toIdx, 0, ids.splice(fromIdx, 1)[0]);
    setLocalOrder(ids);
    // persist on drop end
    const newFull = ids.map(id => orderedMedia.find(m => m.id === id));
    persistOrder(newFull);
  };

  return (
    <div>
      <div className="font-mono-fsyl text-[10px] tracking-[0.4em] text-[var(--fsyl-cyan)] mb-3">/ LIBRARY</div>
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-display font-medium text-4xl tracking-tighter">All entries ({media.length})</h1>
          <div className="font-mono-fsyl text-[10px] tracking-widest text-[var(--fsyl-muted)] mt-1 flex items-center gap-3">
            {items.length !== media.length && <span>SHOWING {items.length} OF {media.length}</span>}
            <span className="text-[var(--fsyl-dim)]">DRAG ANY CARD TO REORDER</span>
            {savingOrder && <span className="text-[var(--fsyl-cyan)]">SAVING ORDER...</span>}
          </div>
        </div>
        <select value={filter} onChange={e=>setFilter(e.target.value)} data-testid="library-filter" className="bg-[var(--fsyl-surface)] border border-white/10 px-3 py-2 font-mono-fsyl text-xs">
          <option value="all">ALL SECTIONS</option>
          {sections.map(s => <option key={s.id} value={s.slug}>{s.name.toUpperCase()}</option>)}
        </select>
      </div>

      <div className="inline-flex border border-white/10 bg-[var(--fsyl-surface)] p-1 mb-8" role="tablist">
        <button
          type="button"
          onClick={() => setDestFilter("all")}
          data-testid="library-dest-all"
          className={`font-mono-fsyl text-[10px] tracking-[0.3em] uppercase px-4 py-2 transition ${
            destFilter === "all" ? "bg-white/10 text-white" : "text-[var(--fsyl-muted)] hover:text-white"
          }`}
        >
          All ({media.length})
        </button>
        <button
          type="button"
          onClick={() => setDestFilter("main")}
          data-testid="library-dest-main"
          className={`font-mono-fsyl text-[10px] tracking-[0.3em] uppercase px-4 py-2 transition ${
            destFilter === "main" ? "bg-white/10 text-white" : "text-[var(--fsyl-muted)] hover:text-white"
          }`}
        >
          Main Archive ({counts.main})
        </button>
        <button
          type="button"
          onClick={() => setDestFilter("vault")}
          data-testid="library-dest-vault"
          className={`font-mono-fsyl text-[10px] tracking-[0.3em] uppercase px-4 py-2 transition ${
            destFilter === "vault" ? "bg-[var(--fsyl-cyan)]/15 text-[var(--fsyl-cyan)]" : "text-[var(--fsyl-muted)] hover:text-white"
          }`}
        >
          ◌ Vault ({counts.vault})
        </button>
      </div>

      {items.length === 0 ? (
        <div className="font-mono-fsyl text-sm text-[var(--fsyl-muted)]">No entries.</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map(m => (
            <div
              key={m.id}
              draggable={!savingOrder}
              onDragStart={(e) => { setDragId(m.id); e.dataTransfer.effectAllowed = "move"; }}
              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
              onDrop={(e) => { e.preventDefault(); moveItem(dragId, m.id); setDragId(null); }}
              onDragEnd={() => setDragId(null)}
              className={`bg-[var(--fsyl-elev)] border border-white/5 relative cursor-grab active:cursor-grabbing transition-opacity ${dragId === m.id ? "opacity-40" : ""} ${dragId && dragId !== m.id ? "hover:border-[var(--fsyl-cyan)]/60" : ""}`}
              data-testid={`library-item-${m.id}`}
            >
              {m.vault && (
                <div className="absolute top-2 right-2 z-10 font-mono-fsyl text-[8px] tracking-widest px-1.5 py-0.5 bg-[var(--fsyl-cyan)]/15 border border-[var(--fsyl-cyan)]/40 text-[var(--fsyl-cyan)]">
                  VAULT
                </div>
              )}
              <div className="absolute top-2 left-2 z-10 font-mono-fsyl text-[8px] tracking-widest px-1.5 py-0.5 bg-black/60 text-[var(--fsyl-muted)] pointer-events-none select-none">
                ⠿ DRAG
              </div>
              <div className="aspect-[4/5] bg-black overflow-hidden pointer-events-none">
                {m.kind === "video" ? (
                  <div className="w-full h-full flex items-center justify-center text-center font-mono-fsyl text-[10px] tracking-widest text-[var(--fsyl-muted)]">
                    ▶ VIDEO
                  </div>
                ) : (
                  <img
                    src={thumbUrl(m.id, m.vault ? "vault" : "site", 480)}
                    alt={m.title || ""}
                    className="w-full h-full object-cover"
                    draggable={false}
                    loading="lazy"
                    decoding="async"
                  />
                )}
              </div>
              <div className="p-3 space-y-2">
                <div className="font-mono-fsyl text-[9px] tracking-widest text-[var(--fsyl-dim)]">{m.kind.toUpperCase()} / {m.section_slug.toUpperCase()}</div>
                <div className="font-display text-sm truncate">{m.title || "Untitled"}</div>
                <div className="flex gap-2 pt-1 flex-wrap">
                  <button onClick={() => setPreviewIdx(items.indexOf(m))} data-testid={`library-preview-${m.id}`} className="text-[10px] font-mono-fsyl tracking-widest border border-[var(--fsyl-cyan)]/30 text-[var(--fsyl-cyan)] px-2 py-1 hover:bg-[var(--fsyl-cyan)]/10">PREVIEW</button>
                  <button onClick={() => setEditing(m)} data-testid={`library-edit-${m.id}`} className="text-[10px] font-mono-fsyl tracking-widest border border-white/10 px-2 py-1 hover:border-[var(--fsyl-cyan)] hover:text-[var(--fsyl-cyan)]">EDIT</button>
                  <button onClick={() => setPendingDelete(m)} data-testid={`library-delete-${m.id}`} className="text-[10px] font-mono-fsyl tracking-widest border border-red-500/30 text-red-400 px-2 py-1 hover:bg-red-500/10">DELETE</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <EditModal item={editing} sections={sections} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}

      {previewIdx !== null && items[previewIdx] && (
        <PreviewModal
          item={items[previewIdx]}
          onClose={() => setPreviewIdx(null)}
          onPrev={() => setPreviewIdx(i => Math.max(0, i - 1))}
          onNext={() => setPreviewIdx(i => Math.min(items.length - 1, i + 1))}
          atStart={previewIdx === 0}
          atEnd={previewIdx === items.length - 1}
          position={`${previewIdx + 1} / ${items.length}`}
          onEdit={() => { setEditing(items[previewIdx]); setPreviewIdx(null); }}
          onDelete={() => { setPendingDelete(items[previewIdx]); setPreviewIdx(null); }}
        />
      )}

      {pendingDelete && (
        <ConfirmModal
          title="Delete this entry?"
          message={`"${pendingDelete.title || "Untitled"}" will be permanently removed. This cannot be undone.`}
          confirmLabel={deleting ? "DELETING..." : "DELETE PERMANENTLY"}
          tone="danger"
          busy={deleting}
          onConfirm={confirmDelete}
          onCancel={() => !deleting && setPendingDelete(null)}
          testid="confirm-delete-media"
        />
      )}
    </div>
  );
};

const PreviewModal = ({ item, onClose, onPrev, onNext, atStart, atEnd, position, onEdit, onDelete }) => {
  const src = fileUrl(item.id, item.vault ? "vault" : "site");

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight" && !atEnd) onNext();
      if (e.key === "ArrowLeft" && !atStart) onPrev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext, atStart, atEnd]);

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex flex-col" data-testid="library-preview-modal" onClick={onClose}>
      {/* Top bar */}
      <div className="flex items-center justify-between p-5 font-mono-fsyl text-[10px] tracking-widest text-[var(--fsyl-muted)]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-4 flex-wrap">
          <span className="text-[var(--fsyl-cyan)]">{position}</span>
          <span>{item.section_slug.toUpperCase()}</span>
          {item.vault && <span className="px-1.5 py-0.5 border border-[var(--fsyl-cyan)]/40 text-[var(--fsyl-cyan)]">VAULT</span>}
          {item.title && <span className="text-white truncate max-w-md">{item.title}</span>}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onEdit} data-testid="preview-edit" className="px-3 py-1.5 border border-white/15 hover:border-[var(--fsyl-cyan)] hover:text-[var(--fsyl-cyan)]">EDIT</button>
          <button onClick={onDelete} data-testid="preview-delete" className="px-3 py-1.5 border border-red-500/30 text-red-400 hover:bg-red-500/10">DELETE</button>
          <button onClick={onClose} data-testid="preview-close" className="px-3 py-1.5 hover:text-[var(--fsyl-cyan)]">✕ CLOSE</button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 flex items-stretch px-6 pb-6 gap-6 min-h-0" onClick={(e) => e.stopPropagation()}>
        {/* Media */}
        <div className="flex-1 flex items-center justify-center min-h-0 relative">
          <button disabled={atStart} onClick={onPrev} data-testid="preview-prev" className="absolute left-0 z-10 disabled:opacity-20 font-mono-fsyl border border-white/10 w-10 h-10 hover:border-[var(--fsyl-cyan)] hover:text-[var(--fsyl-cyan)]">←</button>
          {item.kind === "video" ? (
            <video src={src} className="max-w-[900px] w-full max-h-[80vh] object-contain" controls playsInline preload="metadata" />
          ) : (
            <img src={src} alt={item.title || ""} className="max-h-[85vh] max-w-full object-contain" />
          )}
          <button disabled={atEnd} onClick={onNext} data-testid="preview-next" className="absolute right-0 z-10 disabled:opacity-20 font-mono-fsyl border border-white/10 w-10 h-10 hover:border-[var(--fsyl-cyan)] hover:text-[var(--fsyl-cyan)]">→</button>
        </div>

        {/* Metadata panel */}
        <aside className="hidden md:flex w-80 shrink-0 flex-col overflow-y-auto bg-[var(--fsyl-elev)] border border-white/5 p-5">
          <div className="font-mono-fsyl text-[9px] tracking-[0.3em] text-[var(--fsyl-dim)] mb-2">{item.kind.toUpperCase()}</div>
          {item.title && <h3 className="font-display text-2xl tracking-tight mb-3">{item.title}</h3>}
          {item.reflection && (
            <p className="font-reflection text-base text-[var(--fsyl-text)]/85 leading-relaxed whitespace-pre-line mb-6">
              {item.reflection}
            </p>
          )}
          <div className="space-y-2 font-mono-fsyl text-[10px] tracking-widest text-[var(--fsyl-muted)] mt-auto pt-4 border-t border-white/10">
            {item.camera && <div><span className="text-[var(--fsyl-dim)]">CAMERA / </span>{item.camera.toUpperCase()}</div>}
            {item.captured_at && <div><span className="text-[var(--fsyl-dim)]">CAPTURED / </span>{item.captured_at.toUpperCase()}</div>}
            <div><span className="text-[var(--fsyl-dim)]">SECTION / </span>{item.section_slug.toUpperCase()}</div>
            {(item.tags?.length ?? 0) > 0 && (
              <div className="flex flex-wrap gap-1 pt-2">
                {item.tags.map(t => (
                  <span key={t} className="px-2 py-0.5 border border-white/10 text-[9px]">{t}</span>
                ))}
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
};


const ConfirmModal = ({ title, message, confirmLabel, onConfirm, onCancel, tone = "danger", busy, testid }) => (
  <div className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-md flex items-center justify-center p-6" data-testid={testid} onClick={() => !busy && onCancel()}>
    <div className="bg-[var(--fsyl-elev)] border border-white/10 max-w-md w-full p-6 fade-up" onClick={(e) => e.stopPropagation()}>
      <div className={`font-mono-fsyl text-[10px] tracking-[0.4em] mb-3 ${tone === "danger" ? "text-red-400" : "text-[var(--fsyl-cyan)]"}`}>
        {tone === "danger" ? "// DANGER" : "// CONFIRM"}
      </div>
      <h3 className="font-display font-medium text-2xl tracking-tight mb-3">{title}</h3>
      <p className="font-reflection text-base text-[var(--fsyl-muted)] mb-7">{message}</p>
      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          data-testid={`${testid}-cancel`}
          className="font-mono-fsyl text-[10px] tracking-[0.3em] uppercase px-4 py-2.5 border border-white/15 text-[var(--fsyl-muted)] hover:text-white disabled:opacity-40"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={busy}
          data-testid={`${testid}-confirm`}
          className={`font-mono-fsyl text-[10px] tracking-[0.3em] uppercase px-4 py-2.5 border disabled:opacity-40 ${
            tone === "danger"
              ? "border-red-500/40 text-red-400 hover:bg-red-500/10"
              : "border-[var(--fsyl-cyan)]/40 text-[var(--fsyl-cyan)] hover:bg-[var(--fsyl-cyan)]/10"
          }`}
        >
          {confirmLabel}
        </button>
      </div>
    </div>
  </div>
);

const EditModal = ({ item, sections, onClose, onSaved }) => {
  const [title, setTitle] = useState(item.title || "");
  const [reflection, setReflection] = useState(item.reflection || "");
  const [tags, setTags] = useState((item.tags || []).join(", "));
  const [sectionSlug, setSectionSlug] = useState(item.section_slug);
  const [camera, setCamera] = useState(item.camera || "");
  const [capturedAt, setCapturedAt] = useState(item.captured_at || "");
  const [vault, setVault] = useState(!!item.vault);
  const [busy, setBusy] = useState(false);

  const scopedSections = useMemo(
    () => sections.filter(s => (s.scope || "main") === (vault ? "vault" : "main")),
    [sections, vault]
  );
  const effectiveSection = scopedSections.find(s => s.slug === sectionSlug)
    ? sectionSlug
    : (scopedSections[0]?.slug || "");

  const save = async () => {
    if (!effectiveSection) {
      toast.error(`Create a ${vault ? "vault" : "main"} section first`);
      return;
    }
    setBusy(true);
    try {
      await api.patchMedia(item.id, {
        title: title || null,
        reflection: reflection || null,
        section_slug: effectiveSection,
        tags: tags.split(",").map(t=>t.trim()).filter(Boolean),
        camera: camera || null,
        captured_at: capturedAt || null,
        vault: vault,
      });
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6" data-testid="edit-modal">
      <div className="bg-[var(--fsyl-elev)] border border-white/10 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-white/10 flex justify-between items-center">
          <h3 className="font-display text-2xl tracking-tight">Edit entry</h3>
          <button onClick={onClose} data-testid="edit-close" className="font-mono-fsyl text-xs text-[var(--fsyl-muted)] hover:text-white">✕</button>
        </div>
        <div className="p-6 space-y-5">
          <Field label="SECTION">
            <select value={effectiveSection} onChange={e=>setSectionSlug(e.target.value)} data-testid="edit-section" className="w-full bg-[var(--fsyl-surface)] border border-white/10 px-3 py-3 font-mono-fsyl text-sm" disabled={scopedSections.length === 0}>
              {scopedSections.length === 0 ? (
                <option value="">No {vault ? "vault" : "main"} sections yet</option>
              ) : (
                scopedSections.map(s => <option key={s.id} value={s.slug}>{s.name}</option>)
              )}
            </select>
          </Field>
          <Field label="TITLE">
            <input type="text" value={title} onChange={e=>setTitle(e.target.value)} data-testid="edit-title" className="w-full bg-[var(--fsyl-surface)] border border-white/10 px-3 py-3 font-mono-fsyl text-sm" />
          </Field>
          <Field label="REFLECTION">
            <textarea value={reflection} onChange={e=>setReflection(e.target.value)} rows={6} data-testid="edit-reflection" className="w-full bg-[var(--fsyl-surface)] border border-white/10 px-3 py-3 font-reflection text-base leading-relaxed" />
          </Field>
          <div className="grid grid-cols-3 gap-4">
            <Field label="TAGS"><input value={tags} onChange={e=>setTags(e.target.value)} data-testid="edit-tags" className="w-full bg-[var(--fsyl-surface)] border border-white/10 px-3 py-3 font-mono-fsyl text-sm" /></Field>
            <Field label="CAMERA"><input value={camera} onChange={e=>setCamera(e.target.value)} className="w-full bg-[var(--fsyl-surface)] border border-white/10 px-3 py-3 font-mono-fsyl text-sm" /></Field>
            <Field label="DATE"><input value={capturedAt} onChange={e=>setCapturedAt(e.target.value)} className="w-full bg-[var(--fsyl-surface)] border border-white/10 px-3 py-3 font-mono-fsyl text-sm" /></Field>
          </div>
          <div>
            <div className="font-mono-fsyl text-[10px] tracking-[0.3em] text-[var(--fsyl-muted)] mb-2">DESTINATION</div>
            <div className="inline-flex border border-white/10 bg-[var(--fsyl-surface)] p-1" role="tablist">
              <button
                type="button"
                onClick={() => setVault(false)}
                data-testid="edit-dest-main"
                className={`font-mono-fsyl text-[10px] tracking-[0.3em] uppercase px-4 py-2 transition ${
                  !vault ? "bg-white/10 text-white" : "text-[var(--fsyl-muted)] hover:text-white"
                }`}
              >
                Main Archive
              </button>
              <button
                type="button"
                onClick={() => setVault(true)}
                data-testid="edit-dest-vault"
                className={`font-mono-fsyl text-[10px] tracking-[0.3em] uppercase px-4 py-2 transition ${
                  vault ? "bg-[var(--fsyl-cyan)]/15 text-[var(--fsyl-cyan)]" : "text-[var(--fsyl-muted)] hover:text-white"
                }`}
              >
                ◌ Vault
              </button>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={onClose} className="font-mono-fsyl text-[11px] tracking-widest px-5 py-2.5 border border-white/10 text-[var(--fsyl-muted)] hover:text-white">CANCEL</button>
            <button onClick={save} disabled={busy} data-testid="edit-save" className="font-mono-fsyl text-[11px] tracking-widest px-5 py-2.5 border border-[var(--fsyl-cyan)]/40 text-[var(--fsyl-cyan)] hover:bg-[var(--fsyl-cyan)]/10 disabled:opacity-40">
              {busy ? "SAVING..." : "SAVE"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const SectionsTab = ({ sections, reload }) => {
  const [name, setName] = useState("");
  const [scope, setScope] = useState("main");
  const [busy, setBusy] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const visible = useMemo(
    () => sections.filter(s => (s.scope || "main") === scope),
    [sections, scope]
  );

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.createSection({ name: name.trim(), scope });
      toast.success(`${scope === "vault" ? "Vault" : "Main"} section created`);
      setName("");
      reload();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await api.deleteSection(pendingDelete.id);
      toast.success("Section deleted");
      setPendingDelete(null);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDeleting(false); }
  };

  return (
    <div className="max-w-2xl">
      <div className="font-mono-fsyl text-[10px] tracking-[0.4em] text-[var(--fsyl-cyan)] mb-3">/ SECTIONS</div>
      <h1 className="font-display font-medium text-4xl tracking-tighter mb-6">Sections</h1>
      <p className="font-reflection text-base text-[var(--fsyl-muted)] mb-8">
        Main archive and vault each have their own independent set of sections.
      </p>

      <div className="inline-flex border border-white/10 bg-[var(--fsyl-surface)] p-1 mb-8" role="tablist">
        <button
          type="button"
          onClick={() => setScope("main")}
          data-testid="sections-scope-main"
          className={`font-mono-fsyl text-[10px] tracking-[0.3em] uppercase px-4 py-2 transition ${
            scope === "main" ? "bg-white/10 text-white" : "text-[var(--fsyl-muted)] hover:text-white"
          }`}
        >
          Main Archive
        </button>
        <button
          type="button"
          onClick={() => setScope("vault")}
          data-testid="sections-scope-vault"
          className={`font-mono-fsyl text-[10px] tracking-[0.3em] uppercase px-4 py-2 transition ${
            scope === "vault" ? "bg-[var(--fsyl-cyan)]/15 text-[var(--fsyl-cyan)]" : "text-[var(--fsyl-muted)] hover:text-white"
          }`}
        >
          ◌ Vault
        </button>
      </div>

      <form onSubmit={create} className="flex gap-3 mb-10">
        <input
          type="text"
          value={name}
          onChange={e=>setName(e.target.value)}
          placeholder={`New ${scope === "vault" ? "vault" : "main"} section name`}
          data-testid="section-new-name"
          className="flex-1 bg-[var(--fsyl-surface)] border border-white/10 px-3 py-3 font-mono-fsyl text-sm"
        />
        <button type="submit" disabled={busy} data-testid="section-create" className="font-mono-fsyl text-[11px] tracking-widest px-5 border border-white/15 hover:border-[var(--fsyl-cyan)] hover:text-[var(--fsyl-cyan)] disabled:opacity-40">
          + ADD
        </button>
      </form>

      <div className="space-y-2">
        {visible.map(s => (
          <div key={s.id} className="flex items-center justify-between border border-white/5 bg-[var(--fsyl-elev)] px-4 py-3" data-testid={`section-row-${s.slug}`}>
            <div>
              <div className="font-display text-lg">{s.name}</div>
              <div className="font-mono-fsyl text-[9px] tracking-widest text-[var(--fsyl-dim)]">/{s.slug}</div>
            </div>
            <button onClick={() => setPendingDelete(s)} data-testid={`section-delete-${s.slug}`} className="font-mono-fsyl text-[10px] tracking-widest border border-red-500/30 text-red-400 px-3 py-1.5 hover:bg-red-500/10">
              DELETE
            </button>
          </div>
        ))}
        {visible.length === 0 && <div className="font-mono-fsyl text-sm text-[var(--fsyl-muted)]">No {scope === "vault" ? "vault" : "main"} sections yet.</div>}
      </div>

      {pendingDelete && (
        <ConfirmModal
          title={`Delete "${pendingDelete.name}"?`}
          message={`This will permanently remove the section AND every photo or video inside it. This cannot be undone.`}
          confirmLabel={deleting ? "DELETING..." : "DELETE SECTION + MEDIA"}
          tone="danger"
          busy={deleting}
          onConfirm={confirmDelete}
          onCancel={() => !deleting && setPendingDelete(null)}
          testid="confirm-delete-section"
        />
      )}
    </div>
  );
};

const PasscodeTab = () => {
  const [pc, setPc] = useState("");
  const [busy, setBusy] = useState(false);
  const [vpc, setVpc] = useState("");
  const [vbusy, setVbusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pc.length < 4) {
      toast.error("Passcode must be at least 4 characters");
      return;
    }
    setBusy(true);
    try {
      await api.updatePasscode(pc);
      toast.success("Site passcode updated");
      setPc("");
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  const submitVault = async (e) => {
    e.preventDefault();
    if (vpc.length < 4) {
      toast.error("Passcode must be at least 4 characters");
      return;
    }
    setVbusy(true);
    try {
      await api.updateVaultPasscode(vpc);
      toast.success("Vault passcode updated");
      setVpc("");
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setVbusy(false); }
  };

  return (
    <div className="max-w-xl space-y-14">
      <section>
        <div className="font-mono-fsyl text-[10px] tracking-[0.4em] text-[var(--fsyl-cyan)] mb-3">/ ACCESS — PUBLIC</div>
        <h1 className="font-display font-medium text-4xl tracking-tighter mb-4">Site passcode</h1>
        <p className="font-reflection text-lg text-[var(--fsyl-muted)] mb-6">
          The code visitors enter at FYSL to view the main archive.
        </p>
        <form onSubmit={submit} className="space-y-4">
          <input
            type="text"
            value={pc}
            onChange={e=>setPc(e.target.value)}
            data-testid="passcode-new"
            placeholder="New site passcode"
            className="w-full bg-[var(--fsyl-surface)] border border-white/10 px-3 py-3 font-mono-fsyl text-sm"
          />
          <button type="submit" disabled={busy} data-testid="passcode-save" className="font-mono-fsyl text-[11px] tracking-widest px-6 py-3 border border-white/15 hover:border-[var(--fsyl-cyan)] hover:text-[var(--fsyl-cyan)] disabled:opacity-40">
            {busy ? "UPDATING..." : "→ UPDATE SITE PASSCODE"}
          </button>
        </form>
      </section>

      <section className="border-t border-white/5 pt-12">
        <div className="font-mono-fsyl text-[10px] tracking-[0.4em] text-[var(--fsyl-cyan)] mb-3">/ ACCESS — VAULT</div>
        <h2 className="font-display font-medium text-4xl tracking-tighter mb-4">Vault passcode</h2>
        <p className="font-reflection text-lg text-[var(--fsyl-muted)] mb-6">
          The private code for the vault. Click the <span className="font-mono-fsyl text-[var(--fsyl-cyan)]">FYSL</span> logo on the portfolio to unlock.
        </p>
        <form onSubmit={submitVault} className="space-y-4">
          <input
            type="text"
            value={vpc}
            onChange={e=>setVpc(e.target.value)}
            data-testid="vault-passcode-new"
            placeholder="New vault passcode"
            className="w-full bg-[var(--fsyl-surface)] border border-white/10 px-3 py-3 font-mono-fsyl text-sm"
          />
          <button type="submit" disabled={vbusy} data-testid="vault-passcode-save" className="font-mono-fsyl text-[11px] tracking-widest px-6 py-3 border border-white/15 hover:border-[var(--fsyl-cyan)] hover:text-[var(--fsyl-cyan)] disabled:opacity-40">
            {vbusy ? "UPDATING..." : "→ UPDATE VAULT PASSCODE"}
          </button>
        </form>
      </section>
    </div>
  );
};

const ThemeTab = () => {
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [nowPlaying, setNowPlaying] = useState(false);
  const audioRef = useRef(null);
  const inputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try { setMeta(await api.getTheme()); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const onPick = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("audio/")) {
      toast.error("Pick an audio file (mp3, wav, m4a, ogg, etc.)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.uploadTheme(fd);
      toast.success("Theme uploaded");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const toggleEnabled = async (val) => {
    try {
      await api.toggleTheme(val);
      toast.success(val ? "Theme enabled" : "Theme disabled");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeTheme = async () => {
    if (!window.confirm("Remove theme song?")) return;
    try {
      await api.deleteTheme();
      toast.success("Theme removed");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const togglePreview = () => {
    if (!audioRef.current) return;
    if (nowPlaying) { audioRef.current.pause(); setNowPlaying(false); }
    else { audioRef.current.play().then(() => setNowPlaying(true)).catch(() => {}); }
  };

  return (
    <div className="max-w-2xl" data-testid="theme-tab">
      <div className="font-mono-fsyl text-[10px] tracking-[0.4em] text-[var(--fsyl-cyan)] mb-3">/ THEME SONG</div>
      <h1 className="font-display font-medium text-4xl tracking-tighter mb-4">Passcode-page audio</h1>
      <p className="font-reflection text-lg text-[var(--fsyl-muted)] mb-8">
        Set an optional theme song that loops on the passcode gate at <span className="font-mono-fsyl text-[var(--fsyl-cyan)]">/</span>. Visitors can mute it with the button on the page.
      </p>

      {loading ? (
        <div className="font-mono-fsyl text-sm text-[var(--fsyl-muted)]">LOADING...</div>
      ) : (
        <div className="space-y-8">
          {/* Current status */}
          <div className="border border-white/10 bg-[var(--fsyl-elev)] p-5">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="font-mono-fsyl text-[10px] tracking-widest text-[var(--fsyl-dim)] mb-1">CURRENT</div>
                <div className="font-display text-lg">
                  {meta?.has_audio ? (meta.filename || "theme song") : "None"}
                </div>
                {meta?.has_audio && (
                  <div className="font-mono-fsyl text-[10px] tracking-widest mt-1">
                    STATUS: <span className={meta.enabled ? "text-[var(--fsyl-cyan)]" : "text-[var(--fsyl-dim)]"}>
                      {meta.enabled ? "● ENABLED" : "○ DISABLED"}
                    </span>
                  </div>
                )}
              </div>
              {meta?.has_audio && (
                <div className="flex items-center gap-2">
                  <audio ref={audioRef} src={themeAudioUrl()} onEnded={() => setNowPlaying(false)} preload="metadata" />
                  <button
                    type="button"
                    onClick={togglePreview}
                    data-testid="theme-preview"
                    className="font-mono-fsyl text-[10px] tracking-widest border border-white/15 px-3 py-2 hover:border-[var(--fsyl-cyan)] hover:text-[var(--fsyl-cyan)]"
                  >
                    {nowPlaying ? "◉ PAUSE" : "▶ PREVIEW"}
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleEnabled(!meta.enabled)}
                    data-testid="theme-toggle"
                    className={`font-mono-fsyl text-[10px] tracking-widest px-3 py-2 border ${
                      meta.enabled
                        ? "border-white/15 text-[var(--fsyl-muted)] hover:text-white"
                        : "border-[var(--fsyl-cyan)]/40 text-[var(--fsyl-cyan)] hover:bg-[var(--fsyl-cyan)]/10"
                    }`}
                  >
                    {meta.enabled ? "DISABLE" : "ENABLE"}
                  </button>
                  <button
                    type="button"
                    onClick={removeTheme}
                    data-testid="theme-remove"
                    className="font-mono-fsyl text-[10px] tracking-widest border border-red-500/30 text-red-400 px-3 py-2 hover:bg-red-500/10"
                  >
                    REMOVE
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Upload */}
          <div>
            <div className="font-mono-fsyl text-[10px] tracking-[0.3em] text-[var(--fsyl-muted)] mb-3">
              {meta?.has_audio ? "REPLACE" : "UPLOAD"}
            </div>
            <label
              htmlFor="theme-file-input"
              className="block border border-dashed border-white/15 hover:border-[var(--fsyl-cyan)] hover:bg-white/[0.02] transition cursor-pointer p-8 text-center"
              data-testid="theme-dropzone"
            >
              <div className="font-mono-fsyl text-[10px] tracking-[0.4em] text-[var(--fsyl-cyan)] mb-2">
                {uploading ? "UPLOADING..." : "+ PICK AUDIO FILE"}
              </div>
              <div className="font-reflection text-base text-[var(--fsyl-muted)]">
                MP3, WAV, M4A, OGG — any browser-friendly audio format
              </div>
              <input
                ref={inputRef}
                id="theme-file-input"
                type="file"
                accept="audio/*"
                onChange={(e) => onPick(e.target.files?.[0])}
                data-testid="theme-file"
                className="hidden"
                disabled={uploading}
              />
            </label>
          </div>
        </div>
      )}
    </div>
  );
};

